/**
 * FOLLOWER BOOST — shared UI behaviours (tiny, global)
 *  - scroll reveal for .fb-reveal elements (adds .is-in once)
 *  - stagger: siblings inside [data-fb-stagger] get incremental delays
 *  - respects prefers-reduced-motion (everything visible immediately)
 */
const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function setupReveal(root = document) {
  const items = root.querySelectorAll('.fb-reveal:not([data-fb-observed])');
  if (!items.length) return;

  root.querySelectorAll('[data-fb-stagger]').forEach((group) => {
    const step = parseInt(group.dataset.fbStagger, 10) || 80;
    group.querySelectorAll(':scope > .fb-reveal').forEach((el, i) => {
      el.style.setProperty('--_d', `${i * step}ms`);
    });
  });

  if (reduced || !('IntersectionObserver' in window)) {
    items.forEach((el) => el.classList.add('is-in'));
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        }
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );
  items.forEach((el) => {
    el.dataset.fbObserved = '1';
    io.observe(el);
  });
}

setupReveal();
document.addEventListener('shopify:section:load', (e) => setupReveal(e.target));

/* Touch devices: a tap plays a card's micro-interaction for ~1.4s before following the link */
(function touchFx() {
  if (window.matchMedia('(hover: hover)').matches) return;
  document.addEventListener(
    'touchstart',
    (e) => {
      const card = e.target.closest('.fb-network');
      if (!card) return;
      card.classList.add('is-active');
      setTimeout(() => card.classList.remove('is-active'), 1400);
    },
    { passive: true }
  );
})();

/* Tabs: accessible tablist (arrow keys, aria-selected), no JS = panels stacked */
function setupTabs(root = document) {
  root.querySelectorAll('[data-fb-tabs]:not([data-fb-ready])').forEach((wrap) => {
    wrap.dataset.fbReady = '1';
    const tabs = [...wrap.querySelectorAll('[role="tab"]')];
    const panels = tabs.map((t) => wrap.querySelector('#' + t.getAttribute('aria-controls')));
    const activate = (i, focus) => {
      tabs.forEach((t, j) => {
        const on = i === j;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on);
        t.tabIndex = on ? 0 : -1;
        if (panels[j]) {
          panels[j].hidden = !on;
          panels[j].classList.toggle('is-active', on);
        }
      });
      if (focus) tabs[i].focus();
    };
    tabs.forEach((t, i) => {
      t.addEventListener('click', () => activate(i));
      t.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') activate((i + 1) % tabs.length, true);
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') activate((i - 1 + tabs.length) % tabs.length, true);
      });
    });
  });
}
setupTabs();
document.addEventListener('shopify:section:load', (e) => setupTabs(e.target));

/* Featured services: "see more" on mobile */
function setupSeeMore(root = document) {
  root.querySelectorAll('[data-fb-services-toggle]:not([data-fb-ready])').forEach((btn) => {
    btn.dataset.fbReady = '1';
    const grid = btn.closest('section')?.querySelector('[data-fb-services]');
    if (!grid) return;
    btn.addEventListener('click', () => {
      const open = grid.classList.toggle('is-expanded');
      btn.setAttribute('aria-expanded', open);
      btn.textContent = open ? btn.dataset.less : btn.dataset.more;
      if (open) grid.querySelectorAll('.fb-reveal').forEach((el) => el.classList.add('is-in'));
    });
  });
}
setupSeeMore();
document.addEventListener('shopify:section:load', (e) => setupSeeMore(e.target));
